import React, { useEffect } from "react";
import { match } from "react-router";
import {
  Input,
  InputForm,
  Heading,
  Link,
  Button,
  Paper,
} from "src/components";
import { getRoutePath, MatchParams, Page } from "src/routes";
import { AuthStore, RoutingStore, SpaceStore } from "src/stores";
import { useTranslation } from "react-i18next";
import { inject, observer } from "mobx-react";
import Box from "react-raster";
import { theme } from "src/config";
import toast from "react-hot-toast";
import { Text } from "src/components/text"

const env            = process.env.NODE_ENV;
const presetEmail    = env === 'development' ? "some@some.com" : ''
const presetPassword = env === 'development' ? "some" : ''

export interface ILoginProps {
  authStore:    AuthStore;
  routingStore: RoutingStore;
  match:        match<MatchParams>;  // This is injected so it has to be called match
}

export const Login = inject("authStore", "routingStore")(observer((props: ILoginProps): JSX.Element => {
  const { t, i18n } = useTranslation();
  const [email, setEmail] = React.useState<string>(presetEmail);
  const [password, setPassword] = React.useState<string>(presetPassword);
  const [showPassword, setShowPassword] = React.useState(false);

  const togglePasswordVisiblity = () => {
    setShowPassword(!showPassword);
  };

  const { login, logout, isAuthenticating, isLoggedIn, error_message } =
    props.authStore;

  useEffect(() => {
    if (isLoggedIn) {
      props.routingStore.push(`/${Page.Home}`);
    } else {
      SpaceStore.clearSpaceCookies();
    }
  }, []);

  const submit = async () => {
    const success = isLoggedIn
      ? await logout()
      : await login(email, password);
    if (success) {
      props.routingStore.push(`/${Page.Home}`);
    } else {
      toast.error(t(props.authStore.error_message));
    }
  };

  return (
    <Paper $noShadow style={{ ...theme.paper.small, marginTop: '10vh' }} $center>
      <Box gridRowGap={theme.layout.gutter}>
        <Heading type="h2">{t("login")}</Heading>
        <Heading type="h3">{t("welcome")}</Heading>
        <Box>
          <hr />
        </Box>
        <InputForm gap={"2rem"}>
          <Input
            key          = "email"
            type         = "email"
            label        = {t("email")}
            id           = "email"
            value        = {email}
            onChange     = {(text) => setEmail(text)}
            errorMessage = {t("validation_error", { what: t("email") })}
            showError    = {!!error_message && !email}
          />
          <Input
            key   = "password"
            type  = {showPassword ? "text" : "password"}
            label = {t("password")}
            id    = "password"
            icons = {[
              {
                iconId: showPassword ? "eye" : "eyeClosed",
                action: togglePasswordVisiblity,
              }
            ]}
            value        = {password}
            onChange     = {(text) => setPassword(text)}
            errorMessage = {t("validation_error", { what: t("password") })}
            showError    = {!!error_message && !password}
            onClickIcon  = {togglePasswordVisiblity}
            onKeyDown    = {(e: React.KeyboardEvent<HTMLDivElement>) => {
              if (e.key === "13") login(email, password);
            }}
          />
        </InputForm>
        <Box
          display        = "flex"
          flexDirection  = "row"
          justifyContent = "space-between"
        >
          <Box>
            <Button
              size      = "default"
              variant   = "primary"
              isLoading = {isAuthenticating}
              onClick   = {submit}
            >
              {isLoggedIn ? t("logout") : t("login")}
            </Button>
          </Box>
          <Box>
            <Link
              size    = "small"
              text    = {t("forgot_password")}
              color   = {"secondary"}
              onClick = {() =>
                props.routingStore.push(getRoutePath(Page.ForgotPassword))
              }
            />
          </Box>
        </Box>
        <Box>
          <Text size="small" as="p">
            {t("policies")}{" "}
            <Link size="small" weight="bold" href="" color={"secondary"}>
              {t("terms_of_use")}
            </Link>{" "}
            {t("and")}{" "}
            <Link size="small" weight="bold" href="" color={"secondary"}>
              {t("privacy_policies")}
            </Link>
          </Text>
        </Box>
        <Box>
        <Text size="small" as="p">
            {t("missing_account")}{"? "}
          <Link
            size    = "small"
            text    = {t("create_account")}
            color   = {theme.color.primary7}
            onClick = {() =>
              props.routingStore.push(getRoutePath(Page.CreateAccount))
            }
          />
          </Text>
        </Box>
      </Box>
    </Paper>
  );
}));
